import cx from "classnames";
import { type FC } from "react";

import { InputCheckbox } from "@moment/design-system/InputCheckbox";

import { type QueryBuilderState } from "~/components/canvas/easel/query-builder/QueryBuilderState";
import { type QueryBuilderStateManager } from "~/components/canvas/easel/query-builder/QueryBuilderStateManager";

import { type ValueInspectorRowInfo } from "../traverseValue/ValueInspectorRowInfo";
import { type ValuePathSlice, joinPath } from "../traverseValue/ValuePath";
import { CollapsedValueName } from "./CollapsedValueName";
import { classDoNotCollapseQueryBuilderOnClick } from "./hasClassDoNotCollapseQueryBuilderOnClick";
import { stringifyValueName } from "./stringifyValueName";
import { useFieldActiveQuerySelectedState } from "./useFieldActiveQuerySelectedState";

/**
 * Displays the identifier for a value inside the inspector. For example, a
 * cell's name, the key for a field in an object, an array index, or a key in a
 * map.
 */
export const ValueName: FC<{
	cellID: string;
	row: ValueInspectorRowInfo;
	queryBuilderState: QueryBuilderState | null;
	queryBuilderStateManager: QueryBuilderStateManager;
}> = ({ row, queryBuilderState, queryBuilderStateManager }) => {
	const isEditable = true; //useAppSelector(selectCanvasWritable);

	const { pathFromActiveQueryRoot, fieldSelectionState } = useFieldActiveQuerySelectedState(
		row.path,
		queryBuilderState
	);

	const queryBuilderActive = queryBuilderState?.get("queryingCellId") !== undefined;
	const queryingThisCell = queryBuilderState?.get("queryingCellId") === row.path[0].cellId;
	const isQueryableFieldInActiveQueryRoot = pathFromActiveQueryRoot?.length === 2;
	// eslint-disable-next-line @typescript-eslint/no-non-null-assertion
	const lastComponent = row.path[row.path.length - 1]!;

	if (!isEditable) {
		return (
			<span className="bg-tertiary text-syntax-key  rounded px-1">
				{stringifyValueName(lastComponent)}
			</span>
		);
	}

	if (queryBuilderActive && (!queryingThisCell || !isQueryableFieldInActiveQueryRoot)) {
		return <CollapsedValueName component={lastComponent} />;
	}

	if (queryBuilderActive) {
		return (
			<div
				className={cx(
					classDoNotCollapseQueryBuilderOnClick,
					"flex flex-row items-center space-x-2"
				)}
			>
				<InputCheckbox
					size="sm"
					isSelected={fieldSelectionState === "selected"}
					isDisabled={fieldSelectionState === "unselectable"}
					onClick={() =>
						toggleCheckbox(
							pathFromActiveQueryRoot,
							queryBuilderState,
							queryBuilderStateManager
						)
					}
				/>
				<div>{stringifyValueName(lastComponent)}</div>
			</div>
		);
	}

	return <>{stringifyValueName(lastComponent)}</>;
};

const toggleCheckbox = (
	pathFromActiveQueryRoot: ValuePathSlice | undefined,
	queryBuilderState: QueryBuilderState,
	queryBuilderStateManager: QueryBuilderStateManager
) => {
	if (!pathFromActiveQueryRoot) {
		return;
	}

	const path = joinPath(pathFromActiveQueryRoot.slice(1));
	const field = queryBuilderState.get("fields").get(path);
	if (!field) {
		return;
	}

	switch (queryBuilderState.get("currQueryNodeEditing")) {
		case "select":
			queryBuilderStateManager.setToggleFieldSelected(field);
			return;
		case "orderby": {
			const ordered = queryBuilderState.get("orderedFields");
			if (ordered.orderedFields.has(path)) {
				const i = ordered.orderedFieldOrdering.findIndex((v) => joinPath(v.path) === path);
				if (i < 0) {
					return;
				}
				queryBuilderStateManager.setDeleteOrderedField(i);
			} else {
				queryBuilderStateManager.setAppendOrderedFields([field]);
			}
			return;
		}
		case "groupby": {
			const grouped = queryBuilderState.get("groupedFields");
			if (grouped.groupedFields.has(path)) {
				const i = grouped.groupedFieldOrdering.findIndex((v) => joinPath(v.path) === path);
				if (i < 0) {
					return;
				}
				queryBuilderStateManager.setDeleteGroupedField(i);
			} else {
				queryBuilderStateManager.setAppendGroupedFields([field]);
			}
			return;
		}
	}
};
